import dynamic from 'next/dynamic';
import { FC } from 'react';

import { HIGHLIGHTED_FEATURES } from '../HighlightedFeatureCarousel/HighlightedFeatureCarousel.type';
import { HighlightedFeatureCarouselTile } from '../HighlightedFeatureCarouselTile/HighlightedFeatureCarouselTile';

const imagePathRootV2 = 'https://img.carma.com.au/ui-assets/FeatureCarouselTile/v2';
const imagePathRootV1 = 'https://img.carma.com.au/ui-assets/FeatureCarouselTile';

type HighlightedFeatureCarouselProps = {
  features: HIGHLIGHTED_FEATURES[];
};
type TileImageMappingProps = {
  [key in HIGHLIGHTED_FEATURES]: {
    imgUrl: string;
    title: string;
  };
};

const Carousel = dynamic(() => import('@/general/components/Carousel/Carousel').then((mod) => mod.Carousel), {
  ssr: false,
});

const tileImageMapping: TileImageMappingProps = {
  [HIGHLIGHTED_FEATURES.ADAPTIVE_CRUISE_CONTROL]: {
    imgUrl: `${imagePathRootV2}/advanced-cruise-control.png`,
    title: 'Adaptive cruise control',
  },
  [HIGHLIGHTED_FEATURES.ALLOY_WHEELS]: {
    imgUrl: `${imagePathRootV2}/alloy-wheels.png`,
    title: 'Alloy wheels',
  },
  [HIGHLIGHTED_FEATURES.ANDROID_AUTO]: {
    imgUrl: `${imagePathRootV2}/android-auto.png`,
    title: 'Android Auto',
  },
  [HIGHLIGHTED_FEATURES.APPLE_CARPLAY]: {
    imgUrl: `${imagePathRootV2}/apple-carplay.png`,
    title: 'Apple CarPlay',
  },
  [HIGHLIGHTED_FEATURES.WIRELESS_ANDROID_AUTO]: {
    imgUrl: `${imagePathRootV2}/android-auto.png`,
    title: 'Wireless Android Auto',
  },
  [HIGHLIGHTED_FEATURES.WIRELESS_APPLE_CARPLAY]: {
    imgUrl: `${imagePathRootV2}/apple-carplay.png`,
    title: 'Wireless Apple CarPlay',
  },
  [HIGHLIGHTED_FEATURES.APPLE_CARPLAY_ANDROID_AUTO_LEGACY]: {
    imgUrl: `${imagePathRootV2}/apple-carplay-and-android-auto.png`,
    title: 'Apple CarPlay & Android Auto',
  },
  [HIGHLIGHTED_FEATURES.APPLE_CARPLAY_ANDROID_AUTO]: {
    imgUrl: `${imagePathRootV2}/apple-carplay-and-android-auto.png`,
    title: 'Apple CarPlay & Android Auto',
  },
  [HIGHLIGHTED_FEATURES.WIRELESS_APPLE_CARPLAY_ANDROID_AUTO]: {
    imgUrl: `${imagePathRootV2}/apple-carplay-and-android-auto.png`,
    title: 'Wireless Apple CarPlay & Android Auto',
  },
  [HIGHLIGHTED_FEATURES.WIRELESS_APPLE_CARPLAY_WIRELESS_ANDROID_AUTO]: {
    imgUrl: `${imagePathRootV2}/apple-carplay-and-android-auto.png`,
    title: 'Wireless Apple CarPlay & Wireless Android Auto',
  },
  [HIGHLIGHTED_FEATURES.APPLE_CARPLAY_WIRELESS_ANDROID_AUTO]: {
    imgUrl: `${imagePathRootV2}/apple-carplay-and-android-auto.png`,
    title: 'Apple CarPlay & Wireless Android Auto',
  },
  [HIGHLIGHTED_FEATURES.GPS_NAVIGATION]: {
    imgUrl: `${imagePathRootV2}/gps.png`,
    title: 'GPS navigation',
  },
  [HIGHLIGHTED_FEATURES.HEADS_UP_DISPLAY]: {
    imgUrl: `${imagePathRootV2}/heads-up-display.png`,
    title: 'Heads up display',
  },
  [HIGHLIGHTED_FEATURES.LEATHER_SEATS]: {
    imgUrl: `${imagePathRootV2}/leather-seats.png`,
    title: 'Leather seats',
  },
  [HIGHLIGHTED_FEATURES.REAR_PARKING_CAMERA]: {
    imgUrl: `${imagePathRootV2}/rear-parking-camera.png`,
    title: 'Rear parking camera',
  },
  [HIGHLIGHTED_FEATURES.REAR_PARKING_SENSOR]: {
    imgUrl: `${imagePathRootV2}/rear-park-sensors.png`,
    title: 'Rear parking sensors',
  },
  [HIGHLIGHTED_FEATURES.SUN_ROOF]: {
    imgUrl: `${imagePathRootV2}/sunroof-night.png`,
    title: 'Sunroof',
  },
  [HIGHLIGHTED_FEATURES.HEATED_SEATS]: {
    imgUrl: `${imagePathRootV2}/heated-seats.png`,
    title: 'Heated seats',
  },
  [HIGHLIGHTED_FEATURES.VENTILATED_SEATS]: {
    imgUrl: `${imagePathRootV2}/ventilated-seats.png`,
    title: 'Ventilated seats',
  },
  [HIGHLIGHTED_FEATURES.HEATED_VENTILATED_SEATS_LEGACY]: {
    imgUrl: `${imagePathRootV2}/heated-seats-and-ventilated-seats.png`,
    title: 'Heated & Ventilated seats',
  },
  [HIGHLIGHTED_FEATURES.HEATED_VENTILATED_SEATS]: {
    imgUrl: `${imagePathRootV2}/heated-seats-and-ventilated-seats.png`,
    title: 'Heated & Ventilated seats',
  },
  [HIGHLIGHTED_FEATURES.PUSH_START]: {
    imgUrl: `${imagePathRootV2}/push-start.png`,
    title: 'Push start',
  },
  [HIGHLIGHTED_FEATURES.KEYLESS_ENTRY]: {
    imgUrl: `${imagePathRootV2}/keyless-entry.png`,
    title: 'Keyless entry',
  },
  [HIGHLIGHTED_FEATURES.PUSH_START_KEYLESS_ENTRY_LEGACY]: {
    imgUrl: `${imagePathRootV1}/push-start.png`,
    title: 'Push start & Keyless entry',
  },
  [HIGHLIGHTED_FEATURES.PUSH_START_KEYLESS_ENTRY]: {
    imgUrl: `${imagePathRootV1}/push-start.png`,
    title: 'Push start & Keyless entry',
  },
  [HIGHLIGHTED_FEATURES.ROOF_RAILS]: {
    imgUrl: `${imagePathRootV2}/roof-rails.png`,
    title: 'Roof rails',
  },
  [HIGHLIGHTED_FEATURES.ROOF_RACKS]: {
    imgUrl: `${imagePathRootV2}/roof-racks.png`,
    title: 'Roof racks',
  },
  [HIGHLIGHTED_FEATURES.ROOF_RACKS_RAILS_LEGACY]: {
    imgUrl: `${imagePathRootV1}/roof-racks.png`,
    title: 'Roof racks & Roof rails',
  },
  [HIGHLIGHTED_FEATURES.ROOF_RACKS_RAILS]: {
    imgUrl: `${imagePathRootV1}/roof-racks.png`,
    title: 'Roof racks & Roof rails',
  },
  [HIGHLIGHTED_FEATURES.TOW_PACK]: {
    imgUrl: `${imagePathRootV2}/towpack.png`,
    title: 'Tow pack',
  },
  [HIGHLIGHTED_FEATURES.TOW_PACK_BALL]: {
    imgUrl: `${imagePathRootV2}/towpack.png`,
    title: 'Tow Pack & Tow Ball',
  },
  [HIGHLIGHTED_FEATURES.BLIND_SPOT_MONITORING]: {
    imgUrl: `${imagePathRootV2}/blind-spot-monitoring.png`,
    title: 'Blind spot monitoring',
  },
  [HIGHLIGHTED_FEATURES.SURROUND_VIEW_CAMERA]: {
    imgUrl: `${imagePathRootV2}/surround-view-camera.png`,
    title: '360 degree camera',
  },
};

export const HighlightedFeatureCarousel: FC<HighlightedFeatureCarouselProps> = ({ features }) => {
  const tiles = features
    .filter((feature) => !!tileImageMapping[feature])
    .map((feature) => ({
      id: feature,
      tile: (
        <HighlightedFeatureCarouselTile
          imgUrl={tileImageMapping[feature].imgUrl}
          title={tileImageMapping[feature].title}
        />
      ),
    }));
  return (
    <div className="pt-2 [&_.carouselTile]:flex-[0_0_260px]!">
      <Carousel tiles={tiles} />
    </div>
  );
};
